import type { ResponseZone } from '../types/simulation';

/**
 * ILLUSTRATIVE response model — not clinical data.
 * Oocyte yield saturates with exposure (dose per kg) and is capped by a
 * reserve ceiling that rises with AMH. More FSH cannot exceed the ceiling.
 */
export const TARGET_MIN = 8;
export const TARGET_MAX = 14;
export const SPECTRUM_MAX = 32;

const RESERVE_MAX = 60;
const RESERVE_HALF = 40;
const EXPOSURE_SCALE = 0.2;

/** Fixed starting amount applied to every profile by the ghost (model units). */
export const CONVENTIONAL_DOSE = 11;

export function reserveCeiling(amh: number): number {
  return (RESERVE_MAX * amh) / (amh + RESERVE_HALF);
}

export function predictOocytes(amh: number, dose: number, weight: number): number {
  const exposure = dose / weight;
  return reserveCeiling(amh) * (1 - Math.exp(-exposure / EXPOSURE_SCALE));
}

export function zoneFor(n: number): ResponseZone {
  if (n < TARGET_MIN - 0.5) return 'under';
  if (n >= TARGET_MAX + 0.5) return 'over';
  return 'target';
}

export function oocyteRange(n: number): [number, number] {
  const lo = Math.max(0, Math.round(n * 0.78));
  const hi = Math.max(lo + 1, Math.round(n * 1.22));
  return [lo, hi];
}

export function underRisk(n: number): number {
  return logistic((7.5 - n) / 1.3);
}

export function overRisk(n: number): number {
  return logistic((n - 17) / 2.6);
}

export function riskLabel(v: number): 'Low' | 'Moderate' | 'Elevated' {
  if (v < 0.25) return 'Low';
  if (v < 0.6) return 'Moderate';
  return 'Elevated';
}

function logistic(x: number): number {
  return 1 / (1 + Math.exp(-x));
}
